import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useAuth } from './AuthContext';

interface MediaDevicesContextType {
  localStream: MediaStream | null;
  isVideoEnabled: boolean;
  isAudioEnabled: boolean;
  toggleVideo: () => void;
  toggleAudio: () => void;
  error: string | null;
  requestMediaAccess: () => Promise<boolean | void>;
  hasPermissions: boolean;
  cleanupMedia: () => void;
  optimizeStream: () => void;
  videoQuality: 'low' | 'medium' | 'high';
  setVideoQuality: (quality: 'low' | 'medium' | 'high') => void;
}

// Create the context with a default undefined value
const MediaDevicesContext = createContext<MediaDevicesContextType | undefined>(undefined);

interface MediaDevicesProviderProps {
  children: ReactNode;
}

export function MediaDevicesProvider({ children }: MediaDevicesProviderProps) {
  const { isAuthenticated } = useAuth();
  const [ localStream, setLocalStream ] = useState<MediaStream | null>(null);
  const [ isVideoEnabled, setIsVideoEnabled ] = useState<boolean>(true);
  const [ isAudioEnabled, setIsAudioEnabled ] = useState<boolean>(true);
  const [ error, setError ] = useState<string | null>(null);
  const [ hasPermissions, setHasPermissions ] = useState<boolean>(false);
  const [ videoQuality, setVideoQuality ] = useState<'low' | 'medium' | 'high'>('medium');

  // Track to ensure we only request media once
  const hasRequestedMedia = useRef<boolean>(false);
  const streamCleanupTimer = useRef<NodeJS.Timeout | null>(null);
  const lastActivityTime = useRef<number>(Date.now());

  // Get video constraints based on quality setting
  const getVideoConstraints = useCallback((quality: 'low' | 'medium' | 'high') => {
    const constraints = {
      low: { width: { ideal: 640 }, height: { ideal: 480 }, frameRate: { ideal: 15 } },
      medium: { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } },
      high: { width: { ideal: 1920 }, height: { ideal: 1080 }, frameRate: { ideal: 30 } }
    };

    return {
      facingMode: 'user',
      ...constraints[quality]
    };
  }, []);

  // Clean up media stream and resources
  const cleanupMedia = useCallback(() => {
    if (localStream) {
      localStream.getTracks().forEach(track => {
        track.stop();
        console.log(`Stopped ${track.kind} track`);
      });
      setLocalStream(null);
    }

    if (streamCleanupTimer.current) {
      clearTimeout(streamCleanupTimer.current);
      streamCleanupTimer.current = null;
    }

    setHasPermissions(false);
    hasRequestedMedia.current = false;
  }, [ localStream ]);

  // Optimize stream based on performance and quality
  const optimizeStream = useCallback(async () => {
    if (!localStream) return;

    try {
      // Check if we should adjust quality based on performance
      const videoTrack = localStream.getVideoTracks()[0];
      if (videoTrack) {
        const settings = videoTrack.getSettings();
        console.log('Current video settings:', settings);

        // Auto-adjust quality based on frame rate performance
        if (settings.frameRate && settings.frameRate < 20 && videoQuality !== 'low') {
          console.log('Performance optimization: reducing video quality');
          setVideoQuality('low');
        }
      }
    } catch (err) {
      console.warn('Stream optimization failed:', err);
    }
  }, [ localStream, videoQuality ]);

  // Request access to camera and microphone with quality optimization
  const requestMediaAccess = useCallback(async () => {
    try {
      console.log('Requesting local media stream with quality:', videoQuality);
      lastActivityTime.current = Date.now();

      const videoConstraints = getVideoConstraints(videoQuality);

      const stream = await navigator.mediaDevices.getUserMedia({
        video: videoConstraints,
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100
        }
      });

      setLocalStream(stream);
      setIsVideoEnabled(true);
      setIsAudioEnabled(true);
      setError(null);
      setHasPermissions(true);

      console.log('Local media stream obtained', stream);

      // Set up automatic stream optimization
      setTimeout(() => optimizeStream(), 5000);

      return true;

    } catch (err) {
      let errorMessage = 'Camera and microphone access denied';

      if (err instanceof Error) {
        if (err.name === 'NotAllowedError') {
          errorMessage = 'Camera and microphone permissions denied. Please allow access and refresh the page.';
        } else if (err.name === 'NotFoundError') {
          errorMessage = 'No camera or microphone found on your device.';
        } else if (err.name === 'NotReadableError') {
          errorMessage = 'Camera or microphone is already in use by another application.';
        } else if (err.name === 'OverconstrainedError') {
          errorMessage = 'Camera settings not supported. Trying fallback quality...';
        } else {
          errorMessage = err.message;
        }
      }

      console.error('Media access error:', err);
      setError(errorMessage);
      setHasPermissions(false);

      // Try fallback with lower quality if overconstrained
      if (err instanceof Error && err.name === 'OverconstrainedError' && videoQuality !== 'low') {
        try {
          const fallbackStream = await navigator.mediaDevices.getUserMedia({
            video: getVideoConstraints('low'),
            audio: true
          });

          setLocalStream(fallbackStream);
          setIsVideoEnabled(true);
          setIsAudioEnabled(true);
          setHasPermissions(true);
          setVideoQuality('low');
          console.log('Fallback stream with lower quality obtained');
          return true;
        } catch (fallbackErr) {
          console.error('Fallback quality also failed:', fallbackErr);
        }
      }

      // Try audio-only fallback
      try {
        const audioStream = await navigator.mediaDevices.getUserMedia({
          video: false,
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          }
        });

        setLocalStream(audioStream);
        setIsVideoEnabled(false);
        setIsAudioEnabled(true);
        setHasPermissions(true);
        setError('Video unavailable - audio only mode');
        console.log('Audio-only fallback stream obtained');
        return true;
      } catch (audioErr) {
        console.error('Audio fallback also failed:', audioErr);
        setError('Unable to access camera or microphone. Please check your permissions.');
        setHasPermissions(false);
        return false;
      }
    }
  }, [ videoQuality, getVideoConstraints, optimizeStream ]);

  // Initialize media access only when user is authenticated
  useEffect(() => {
    const fetchMedia = async () => {
      // Only request media if authenticated and not already requested
      if (isAuthenticated && !hasRequestedMedia.current) {
        await requestMediaAccess();
        hasRequestedMedia.current = true;
      }
    };

    fetchMedia();

    // Clean up function to stop all tracks
    return () => {
      if (localStream) {
        localStream.getTracks().forEach(track => track.stop());
        console.log('Cleaning up local media stream');
      }
    };
  }, [ isAuthenticated, localStream, requestMediaAccess ]);

  // Toggle video on/off
  const toggleVideo = useCallback(() => {
    if (!localStream) return;

    const videoTracks = localStream.getVideoTracks();

    videoTracks.forEach(track => {
      track.enabled = !track.enabled;
    });

    setIsVideoEnabled(videoTracks.some(track => track.enabled));
  }, [ localStream ]);

  // Toggle audio on/off
  const toggleAudio = useCallback(() => {
    if (!localStream) return;

    const audioTracks = localStream.getAudioTracks();

    audioTracks.forEach(track => {
      track.enabled = !track.enabled;
    });

    setIsAudioEnabled(audioTracks.some(track => track.enabled));
  }, [ localStream ]);

  // Memory management for long sessions
  useEffect(() => {
    const CLEANUP_INTERVAL = 30 * 60 * 1000; // 30 minutes
    const IDLE_THRESHOLD = 10 * 60 * 1000; // 10 minutes of inactivity

    const memoryCleanupTimer = setInterval(() => {
      const now = Date.now();
      const timeSinceActivity = now - lastActivityTime.current;

      if (timeSinceActivity > IDLE_THRESHOLD && localStream) {
        console.log('Detected idle session, optimizing memory usage');
        optimizeStream();

        // Force garbage collection if available (development only)
        if (window.gc && typeof window.gc === 'function') {
          window.gc();
        }
      }
    }, CLEANUP_INTERVAL);

    return () => clearInterval(memoryCleanupTimer);
  }, [ localStream, optimizeStream ]);

  // Update activity time on user interaction
  const updateActivity = useCallback(() => {
    lastActivityTime.current = Date.now();
  }, []);

  // Track user activity
  useEffect(() => {
    const events = [ 'mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart' ];

    events.forEach(event => {
      document.addEventListener(event, updateActivity, true);
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, updateActivity, true);
      });
    };
  }, [ updateActivity ]);

  // Create the context value object
  const contextValue: MediaDevicesContextType = {
    localStream,
    isVideoEnabled,
    isAudioEnabled,
    toggleVideo,
    toggleAudio,
    error,
    requestMediaAccess,
    hasPermissions,
    cleanupMedia,
    optimizeStream,
    videoQuality,
    setVideoQuality
  };

  return (
    <MediaDevicesContext.Provider value={contextValue}>
      {children}
    </MediaDevicesContext.Provider>
  );
}

// Custom hook to use the media devices context
export function useMediaDevices(): MediaDevicesContextType {
  const context = useContext(MediaDevicesContext);

  if (context === undefined) {
    throw new Error('useMediaDevices must be used within a MediaDevicesProvider');
  }

  return context;
}